// Formatting and parsing helpers for the employee profile page.
//
// These were previously duplicated inside the per-field inline editors
// (EditableField, AddressEditableField, EditableMonthDayField). The profile now
// edits every field from one form, so the parsing lives here instead.

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const EMERGENCY_RELATIONSHIP_OPTIONS = [
  { label: 'Spouse', value: 'spouse' },
  { label: 'Partner', value: 'partner' },
  { label: 'Parent', value: 'parent' },
  { label: 'Family Member', value: 'family_member' },
  { label: 'Other', value: 'other' },
];

export const emergencyRelationshipLabel = (value) => {
  const match = EMERGENCY_RELATIONSHIP_OPTIONS.find((o) => o.value === value);
  return match ? match.label : '';
};

/* ---------------------------------------------------------------- phone --- */

export const digitsOnly = (value) => String(value || '').replace(/\D+/g, '');

// Progressive formatting as the user types: "(555", "(555) 867", "(555) 867-5309".
export const formatPhone = (value) => {
  const d = digitsOnly(value);
  if (!d) return '';

  const dd = d.slice(0, 10);
  const a = dd.slice(0, 3);
  const b = dd.slice(3, 6);
  const c = dd.slice(6, 10);
  if (dd.length <= 3) return `(${a}`;
  if (dd.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
};

// Anything shorter than a full number is shown as stored rather than half
// formatted — legacy rows predate the 10-digit rule.
export const formatPhoneForDisplay = (value) => {
  const d = digitsOnly(value);
  if (!d) return '';
  if (d.length < 10) return String(value);
  return formatPhone(d);
};

/* -------------------------------------------------------------- address --- */

export const normalizeState = (value) =>
  String(value || '').replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase();

export const normalizeZip = (value) => digitsOnly(value).slice(0, 5);

const parseCityStateZip = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { city: '', state: '', zip: '' };

  // "City, ST 12345" or "City ST 12345"
  const m = raw.match(/^(.+?)(?:,\s*)?([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?$/);
  if (m) {
    return {
      city: String(m[1] || '').trim(),
      state: normalizeState(m[2]),
      zip: normalizeZip(m[3]),
    };
  }

  return { city: raw, state: '', zip: '' };
};

// The column holds one free-text string; split it back into parts so the form
// can present real fields.
export const parseAddressString = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { street: '', city: '', state: '', zip: '' };

  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const street = lines[0];
    const rest = lines.slice(1).join(' ');
    const { city, state, zip } = parseCityStateZip(rest);
    return { street, city, state, zip };
  }

  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const street = parts[0];
    const cityPart = parts[1] || '';
    const tail = parts.slice(2).join(' ').trim();

    if (tail) {
      const { city, state, zip } = parseCityStateZip(`${cityPart} ${tail}`.trim());
      return { street, city: city || cityPart, state, zip };
    }

    const { city, state, zip } = parseCityStateZip(cityPart);
    return { street, city, state, zip };
  }

  return { street: raw, city: '', state: '', zip: '' };
};

export const formatCombinedAddress = ({ street, city, state, zip }) => {
  const s = String(street || '').trim();
  const c = String(city || '').trim();
  const st = normalizeState(state);
  const z = normalizeZip(zip);

  let line2 = c;
  if (st) line2 = line2 ? `${line2}, ${st}` : st;
  if (z) line2 = line2 ? `${line2} ${z}` : z;

  if (s && line2) return `${s}\n${line2}`;
  return s || line2 || '';
};

// "Brooklyn, NY" — the one line of an address worth showing in the header.
export const addressLocality = (value) => {
  const { city, state } = parseAddressString(value);
  if (city && state) return `${city}, ${state}`;
  return city || state || '';
};

/* ------------------------------------------------------------- birthday --- */

export const daysInMonth = (month) => {
  const m = Number(month);
  if (!Number.isFinite(m) || m < 1 || m > 12) return 31;
  return new Date(2000, m, 0).getDate(); // month is 1-based here
};

export const parseMonthDay = (value) => {
  if (!value) return { month: null, day: null };
  const m = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { month: null, day: null };

  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(month) || !Number.isFinite(day)) return { month: null, day: null };
  return { month, day };
};

// The year is meaningless — birthdays are stored as a month/day on a fixed year.
export const toBirthdayDateString = (month, day) => {
  const m = Number(month);
  const d = Number(day);
  if (!m || !d) return null;
  if (d < 1 || d > daysInMonth(m)) return null;
  return `2000-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

export const formatBirthday = (value) => {
  const { month, day } = parseMonthDay(value);
  if (!month || !day) return '';
  return `${MONTHS[month - 1] || month} ${day}`;
};

/* ---------------------------------------------------------------- dates --- */

export const parseLocalDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

// The date-only shape the API stores and the profile form holds.
export const toDateInputValue = (value) => {
  const d = parseLocalDateOnly(value);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export const formatDateDisplay = (value) => {
  const d = parseLocalDateOnly(value);
  if (!d) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const pluralize = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

// Whole years and months since the hire date, or null when it can't be computed
// (no hire date, or a date in the future).
export const employmentTenure = (hireDateValue) => {
  const start = parseLocalDateOnly(hireDateValue);
  if (!start) return null;

  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (end.getTime() < start.getTime()) return null;

  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  if (!Number.isFinite(months) || months < 0) return null;

  return { years: Math.floor(months / 12), months: months % 12 };
};

export const formatTenure = (hireDateValue) => {
  const tenure = employmentTenure(hireDateValue);
  if (!tenure) return '';
  return `${pluralize(tenure.years, 'year')}, ${pluralize(tenure.months, 'month')}`;
};

/* --------------------------------------------------------------- values --- */

export const formatCurrency = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

export const formatPercent = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n}%`;
};
